import React, { useEffect, useState } from 'react'
import { Upload, message } from 'antd'
import { useDispatch } from 'react-redux'

import { MAX_IMPORT_FILE_SIZE } from 'constants/app'
import { validateImportCsv } from 'store/assetImport/actions'
import { CSVCharset } from 'models/csvCharset'
import { isSupportedCharset, validateFileSize } from 'utils/FileHelper'
import * as recalculateApi from 'api/app/recalculate'

import { DownloadLink, Button } from 'components/atoms'
import { ErrorsList } from 'components/molecules'
import { Wrapper, Header, Body } from './styles'
import useResource from 'lib/hooks/useResource'
import { showErrorsAppCall } from 'store/app/actions'
import { getRecalculateError } from 'utils/AssetHelper'

import { getNs } from 'aa_common/front-end/i18n-config'

const { t, useNsReady } = getNs([
  'components/molecules/auto-complete-select',
  'common',
  'components/organisms/assets/asset-import-form',
  'components/molecules/modals/import-modal',
])

const { Dragger } = Upload

const AssetImportForm: React.FC = () => {
  useNsReady()
  const dispatch = useDispatch()
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [{ data: recalculateHistory }, getRecalculateHistory] = useResource(recalculateApi.getHistory)

  const beforeUpload = (file: File): boolean => {
    if (!validateFileSize(file, MAX_IMPORT_FILE_SIZE)) {
      message.error(t('msg_file_size_limit'), 2)
    } else {
      setUploadedFile(file)
    }

    return false
  }

  const [preImportErrs, setPreImportErrs] = useState<string | string[] | null>()
  const handleSubmit = () => {
    if (uploadedFile) {
      isSupportedCharset(uploadedFile).then(({ charset, isSupported, notSupportedMsg }) => {
        notSupportedMsg && setPreImportErrs(notSupportedMsg)
        if (isSupported) {
          dispatch(validateImportCsv(charset as CSVCharset, uploadedFile))
        }
      })
    }
  }

  useEffect(() => {
    getRecalculateHistory()
    const recalculateError = getRecalculateError(recalculateHistory)
    recalculateError && dispatch(showErrorsAppCall(recalculateError))
  }, []) //eslint-disable-line

  return (
    <Wrapper>
      {preImportErrs && <ErrorsList errors={preImportErrs} />}
      <Header>{t('header_1')}</Header>
      <Body>
        <div>
          <a target="_blank" href="/files/import_samples/fixed_assets_onboarding_sjis.csv">
            <DownloadLink>{t('download_shiftjis')}</DownloadLink>
          </a>
        </div>
        <div style={{ marginTop: 5 }}>
          <a target="_blank" href="/files/import_samples/fixed_assets_onboarding_utf8.csv">
            <DownloadLink>{t('download_utf8')}</DownloadLink>
          </a>
        </div>
        <div style={{ marginTop: 12 }} className="bold">
          {t('precaution')}
        </div>
        <div style={{ whiteSpace: 'pre-wrap', marginTop: 5 }}>{t('precaution_content')}</div>
      </Body>
      <Header>{t('header_2')}</Header>
      <Body>
        <div className="bold">{t('precaution_import')}</div>
        <div>{t('precaution_import_desc')}</div>
        <div>
          <Upload accept=".csv" fileList={[]} beforeUpload={beforeUpload} multiple={false}>
            <Button color="grey">{t('select_attachment')}</Button>
            <label style={{ marginLeft: 5 }}>{uploadedFile ? uploadedFile.name : t('not_selected')}</label>
          </Upload>
          <Dragger multiple={false} style={{ marginTop: 10, height: 122 }} beforeUpload={beforeUpload} accept=".csv">
            <p className="ant-upload-text">{t('drag_and_drop_here')}</p>
          </Dragger>
          <div className="action">
            <Button onClick={handleSubmit} disabled={!uploadedFile}>
              {t('actions.import')}
            </Button>
          </div>
        </div>
      </Body>
    </Wrapper>
  )
}

export default AssetImportForm
